import get from "lodash/get";
import { ICampaignServer } from "model/campaigns";
import { LoadingState } from "model/Types/loading";
import {
  CreativeStatus,
  ICreativeRichMedia,
  TCreative,
  constructCacheSrcByUrl,
  getAssetsToCreativesMapAsync,
  getPlayablesToCreativesMap as getPlayablesToCreatives,
} from "model/creative";
import {
  LineitemStatus,
  getLineitemStatus,
} from "model/lineitems/lineitemStatus";
import {
  ICampaignsLineitemStatusServerObject,
  getCampaignsLineitemsStatusServerObject,
} from "redux/campaignStatus/statusServerSelectors";
import {
  getActiveCampaigns,
  getAllCampaigns,
  getCampaignsLoadingState,
} from "redux/campaigns/selectors";
import { IRootState } from "redux/reducer";
import { createDeepEqualSelector } from "redux/utils";
import { createSelector } from "reselect";

export type ICreativesObject = { [cId: string]: TCreative };

const getCreativeParamId = (state: IRootState, params: any) =>
  get(params, "creativeId", null);

const getCreativeId = (state: IRootState, creativeId: string) => creativeId;

export const getActiveCreativesObject = (state: IRootState) =>
  get(state, "creatives.active", {}) as ICreativesObject;

export const getArchivedCreativesObject = (state: IRootState) =>
  get(state, "creatives.archived", {}) as ICreativesObject;

export const getExpandedCreatives = (state: IRootState) =>
  get(state, "creatives.expanded", {});

export const getShowArchived = (state: IRootState): boolean =>
  get(state, "creatives.showArchived");

export const getErrorLoadingCreativesApprovals = (state: IRootState) =>
  get(state, "creatives.errorLoadingCreativesApprovals");

export const getFoldersFilter = (state: IRootState): string =>
  get(state, "creatives.filter.folders");

export const getLooseFilter = (state: IRootState): string =>
  get(state, "creatives.filter.loose");

export const getErrorLoadingActiveCreatives = (state: IRootState) =>
  get(state, "creatives.errorLoadingActiveCreatives");

export const getLastActiveCreativesFetch = (state: IRootState) =>
  get(state, "creatives.lastActiveCreativesFetch");

export const getLastArchivedCreativesFetch = (state: IRootState) =>
  get(state, "creatives.lastArchivedCreativesFetch");

export const getLastCreativesApprovalFetch = (state: IRootState) =>
  get(state, "creatives.lastCreativesApprovalFetch");

export const getCacheVersions = (state: IRootState) =>
  get(state, "creatives.cacheVersions");

export const getIABCategories = (state: IRootState) =>
  get(state, "creatives.IABCategories");
export const getIABCategoriesOptions = createSelector(
  getIABCategories,
  (iabCategoriesResponse) => {
    const items = iabCategoriesResponse.items;
    if (!Object.keys(items)?.length) return [];

    const options = Object.keys(items)
      .map((key) => ({
        label: key,
        value: items[key],
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, "en", {
          numeric: true,
          sensitivity: "base",
        })
      );

    return options;
  }
);

export const getActiveCreatives = createDeepEqualSelector(
  getActiveCreativesObject,
  (active: ICreativesObject) => Object.values(active)
);
export const getArchivedCreatives = createDeepEqualSelector(
  getArchivedCreativesObject,
  (archived: ICreativesObject) => Object.values(archived)
);

export const getAllCreatives = createDeepEqualSelector(
  getActiveCreatives,
  getArchivedCreatives,
  (activeCreatives: TCreative[], archivedCreatives: TCreative[]) => [
    ...activeCreatives,
    ...archivedCreatives,
  ]
);

export const getAllCreativesObject = createDeepEqualSelector(
  getActiveCreativesObject,
  getArchivedCreativesObject,
  (activeCreatives: ICreativesObject, archivedCreaties: ICreativesObject) => ({
    ...activeCreatives,
    ...archivedCreaties,
  })
);

export const getCreativeByParamId = createDeepEqualSelector(
  getCreativeParamId,
  getAllCreativesObject,
  (creativeId: string, creatives: ICreativesObject): TCreative =>
    creatives[creativeId]
);

export const getCreativeById = createDeepEqualSelector(
  getCreativeId,
  getAllCreativesObject,
  (creativeId, creatives) => creatives[creativeId]
);

export const getAllTags = createDeepEqualSelector(
  getAllCreatives,
  (creatives) =>
    [...new Set(creatives.map((c) => (c as ICreativeRichMedia).tag))].filter(
      Boolean
    ) as string[]
);

export const getActiveTags = createDeepEqualSelector(
  getActiveCreatives,
  (creatives) =>
    [...new Set(creatives.map((c) => c.tag))].filter(Boolean) as string[]
);

export const statusObjectSelector = (
  allCampaigns: ICampaignServer[],
  campaignsLineitemStatusObject: ICampaignsLineitemStatusServerObject
) => {
  const result = allCampaigns.reduce((statusObj, c) => {
    const { lineitems } = c;
    const lineitemsStatusObject =
      campaignsLineitemStatusObject[c.id]?.lineitems;

    if (!lineitems?.length) {
      return statusObj;
    }

    lineitems.forEach((l) => {
      const lState = lineitemsStatusObject?.[l.id]?.lineitemState;
      const lRunState = lState?.runState;
      const lStatus = lRunState
        ? getLineitemStatus(lRunState, new Date(l.finish))
        : LineitemStatus.Unknown;

      const creativeIds = l.creativeIds;
      if (!creativeIds?.length) {
        return;
      }

      creativeIds.forEach((crId) => {
        const associatedLineitem = {
          campaignId: c.id,
          campaignName: c.name,
          id: l.id,
          name: l.name,
          status: lStatus,
          isArchived: lState?.archived,
        };

        if (!statusObj[crId]) {
          statusObj[crId] = {
            status:
              lStatus === LineitemStatus.Unknown
                ? CreativeStatus.Loading
                : lStatus === LineitemStatus.Running
                  ? CreativeStatus.Live
                  : CreativeStatus.NotLive,
            associatedLineitems: [associatedLineitem],
          };
        } else {
          if (lStatus === LineitemStatus.Running) {
            statusObj[crId].status = CreativeStatus.Live;
          }
          statusObj[crId].associatedLineitems.push(associatedLineitem);
        }
      });
    });

    return statusObj;
  }, {});

  return result;
};
export const getCreativesStatusObject = createDeepEqualSelector(
  getAllCampaigns,
  getCampaignsLineitemsStatusServerObject,
  statusObjectSelector
);

export const getAllCreativesStatusObject = createSelector(
  getActiveCampaigns,
  getCampaignsLineitemsStatusServerObject,
  getActiveCreatives,
  getCampaignsLoadingState.active,
  (
    activeCampaigns,
    lineitemStatusObject,
    activeCreatives,
    [campaignsLoadingState]
  ) => {
    const isCampaignsLoading =
      campaignsLoadingState === LoadingState.initial ||
      campaignsLoadingState === LoadingState.loading;

    const result = statusObjectSelector(activeCampaigns, lineitemStatusObject);
    activeCreatives.forEach((c) => {
      if (!result[c.id]) {
        result[c.id] = {
          status: isCampaignsLoading
            ? CreativeStatus.Loading
            : CreativeStatus.NotLive,
          associatedLineitems: [],
        };
      }
    });
    return result;
  }
);

export const getCreativesStatusObjectBySingleCampaign = createSelector(
  [
    (state, campaignId) => campaignId,
    getAllCampaigns,
    getCampaignsLineitemsStatusServerObject,
  ],
  (
    campaignId,
    allCampaigns: ICampaignServer[],
    statusObject: ICampaignsLineitemStatusServerObject
  ) => {
    const campaigns = campaignId
      ? allCampaigns.filter((c) => c.id === campaignId)
      : allCampaigns;
    return statusObjectSelector(campaigns, statusObject);
  }
);

const getSrc = (state: IRootState, src: string) => src;
export const getCacheSrcByUrl = createDeepEqualSelector(
  getSrc,
  getCacheVersions,
  constructCacheSrcByUrl
);

export const getAssetsToCreativesMap = createDeepEqualSelector(
  getActiveCreatives,
  getAssetsToCreativesMapAsync
);

export const getPlayablesToCreativesMap = createDeepEqualSelector(
  getActiveCreatives,
  getPlayablesToCreatives
);

export interface ICreativeUsageInLineItem {
  campaignId: string;
  campaignName: string;
  lineItemId: string;
  lineItemName: string;
  isArchived: boolean;
  skanId?: number;
}

export interface ICreativeUsageMap {
  [creativeId: string]: ICreativeUsageInLineItem[];
}
export const getCreativeUsageInSkanData = createDeepEqualSelector(
  getAllCampaigns,
  (allCampaigns: ICampaignServer[]) => {
    const usageMap: ICreativeUsageMap = {};

    allCampaigns.forEach((campaign) => {
      if (!campaign.lineitems) return;

      campaign.lineitems.forEach((lineItem) => {
        const creativeData = lineItem.skAdNetworkConfiguration?.creativeData;
        if (creativeData) {
          creativeData.forEach((creative) => {
            if (!usageMap[creative.creativeId]) {
              usageMap[creative.creativeId] = [];
            }

            usageMap[creative.creativeId].push({
              campaignId: campaign.id,
              campaignName: campaign.name,
              lineItemId: lineItem.id,
              lineItemName: lineItem.name,
              isArchived: !!lineItem.archivedDate,
              skanId: creative.skanId,
            });
          });
        }
      });
    });

    return usageMap;
  }
);
